<script lang="ts">
/**
 * Sidebar adapter that wraps Nuxt UI's `UDashboardSidebar`.
 *
 * Forwards all slots (`header`, `default`, `footer`) and key props. On desktop
 * (`lg:+`) renders a collapsible/resizable sidebar. On mobile, renders
 * as an overlay slideover when `open` is true.
 *
 * @example
 * ```vue
 * <AppSidebar v-model:open="open" collapsible resizable>
 *   <template #header="{ collapsed }">...</template>
 *   <template #default="{ collapsed }">...</template>
 *   <template #footer="{ collapsed }">...</template>
 * </AppSidebar>
 * ```
 */
export interface AppSidebarProps {
  /** Sidebar identifier for state persistence. @defaultValue `"default"` */
  id?: string
  /** Allow the sidebar to collapse to icon-only mode. @defaultValue `false` */
  collapsible?: boolean
  /** Allow the sidebar to be resized by dragging. @defaultValue `false` */
  resizable?: boolean
}
</script>

<script setup lang="ts">
withDefaults(defineProps<AppSidebarProps>(), {
  id: "default",
  collapsible: false,
  resizable: false,
})

const open = defineModel<boolean>("open", { default: false })
</script>

<template>
  <UDashboardSidebar
    :id="id"
    v-model:open="open"
    :collapsible="collapsible"
    :resizable="resizable"
    class="bg-elevated/25"
    :ui="{
      content: 'pt-[var(--safe-top)]',
      footer: 'lg:border-t lg:border-default',
    }"
  >
    <template #header="slotProps">
      <slot name="header" v-bind="slotProps" />
    </template>

    <template #default="slotProps">
      <slot v-bind="slotProps" />
    </template>

    <template #footer="slotProps">
      <slot name="footer" v-bind="slotProps" />
    </template>
  </UDashboardSidebar>
</template>
